import { lazy, Suspense } from 'react';
import {
    BrowserRouter,
    Route,
    Routes,
    Navigate,
    useLocation,
} from 'react-router-dom';
import usePageTracking from '@/hooks/usePageTracking';

// Main Tailwind theme
import "./index.css";

// Lazy load all route components
const Home = lazy(() => import("./pages/Home"));
const Login = lazy(() => import("./pages/Login"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Quotes = lazy(() => import("./pages/dashboard/Quotes"));
const QuotesTable = lazy(() => import("./pages/dashboard/quotes/QuotesTable"));
const Create = lazy(() => import("./pages/dashboard/quotes/Create"));
const ViewQuote = lazy(() => import("./pages/dashboard/quotes/ViewQuote"));
const ViewQuoteNycm = lazy(() => import("./pages/dashboard/quotes/ViewQuoteNycm"));
const Policies = lazy(() => import("./pages/dashboard/Policies"));
const PoliciesTable = lazy(() => import("./pages/dashboard/policies/PoliciesTable"));
const ViewPolicy = lazy(() => import("./pages/dashboard/policies/ViewPolicy"));
const Settings = lazy(() => import("./pages/dashboard/Settings"));
const Users = lazy(() => import("./pages/dashboard/Users"));
const Agencies = lazy(() => import("./pages/dashboard/Agencies"));
const Import = lazy(() => import("./pages/dashboard/admin/Import"));

const RequireAuth = ({ children }) => {
    let location = useLocation();
    const token = localStorage.getItem("digiquoteUser");

    if (!token) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return children;
};

const AppRoutes = () => {
    usePageTracking();

    return (
        <Routes>
                    <Route path="/" element={
                        <Suspense fallback={null}>
                            <Home />
                        </Suspense>
                    } />
                    <Route
                        path="dashboard"
                        element={<RequireAuth>
                            <Suspense fallback={null}>
                                <Dashboard />
                            </Suspense>
                        </RequireAuth>}>
                        <Route path="quotes" element={
                            <Suspense fallback={null}>
                                <Quotes />
                            </Suspense>
                        }>
                            <Route index element={
                                <Suspense fallback={null}>
                                    <QuotesTable />
                                </Suspense>
                            } />
                            <Route path="create" element={
                                <Suspense fallback={null}>
                                    <Create />
                                </Suspense>
                            } />
                            <Route path="view/comp/:docId" element={
                                <Suspense fallback={null}>
                                    <ViewQuote />
                                </Suspense>
                            } />
                            <Route path="view/bop/:docId" element={
                                <Suspense fallback={null}>
                                    <ViewQuoteNycm />
                                </Suspense>
                            } />
                        </Route>
                        <Route path="policies" element={
                            <Suspense fallback={null}>
                                <Policies />
                            </Suspense>
                        }>
                            <Route index element={
                                <Suspense fallback={null}>
                                    <PoliciesTable />
                                </Suspense>
                            } />
                            <Route path="view/comp/:docId" element={
                                <Suspense fallback={null}>
                                    <ViewPolicy />
                                </Suspense>
                            } />
                            <Route path="view/bop/:docId" element={
                                <Suspense fallback={null}>
                                    <ViewPolicy />
                                </Suspense>
                            } />
                        </Route>
                        <Route path="settings" element={
                            <Suspense fallback={null}>
                                <Settings />
                            </Suspense>
                        } />
                        <Route path="users" element={
                            <Suspense fallback={null}>
                                <Users />
                            </Suspense>
                        } />
                        <Route path="agencies" element={
                            <Suspense fallback={null}>
                                <Agencies />
                            </Suspense>
                        } />
                        <Route path="admin/import" element={
                            <Suspense fallback={null}>
                                <Import />
                            </Suspense>
                        } />
                        <Route index element={<Navigate to="quotes" />} />
                        <Route path="*" element={<Navigate to="quotes" />} />
                    </Route>
                    <Route
                        path="/login"
                        element={
                            <Suspense fallback={null}>
                                <Login />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/settings"
                        element={<Navigate to="/dashboard/settings" />}
                    />
                    <Route
                        path="/create"
                        element={<Navigate to="/dashboard/quotes/create" />}
                    />
                    <Route
                        path="/quotes"
                        element={<Navigate to="/dashboard/quotes" />}
                    />
                    <Route
                        path="/quotes/comp/:docId"
                        element={<Navigate to="/dashboard/quotes/view/comp/:docId" />}
                    />
                    <Route
                        path="/quotes/bop/:docId"
                        element={<Navigate to="/dashboard/quotes/view/bop/:docId" />}
                    />
                    <Route
                        path="/policies"
                        element={<Navigate to="/dashboard/policies" />}
                    />
                    <Route
                        path="/policies/comp/:docId"
                        element={<Navigate to="/dashboard/quotes/view/comp/:docId" />}
                    />
                    <Route
                        path="/policies/bop/:docId"
                        element={<Navigate to="/dashboard/quotes/view/bop/:docId" />}
                    />
                    <Route path="*" element={<Navigate to="/" />} />
                </Routes>
    );
};

const App = () => {
    return (
        <div>
            <BrowserRouter>
                <AppRoutes />
            </BrowserRouter>
        </div>
    );
};

export default App;
